import { RefObject, useEffect, useLayoutEffect, useRef } from "react"
import { roundZoom, wheelDeltaPx, zoomByWheel } from "../helpers/wheelZoom.helpers"

/**
 * Cmd/Ctrl + mouse wheel and touchpad pinch zoom over one element.
 *
 * The listeners sit on the element itself, so which view zooms is decided by where the
 * pointer is: over the canvas the canvas zooms, over the timeline the timeline does. Anywhere
 * else the browser keeps its own page zoom.
 *
 * Three inputs, one behaviour:
 *
 * - Cmd/Ctrl + mouse wheel — a plain `wheel` event with the modifier held
 * - pinch in Chrome, Edge and Firefox — ALSO a `wheel` event: the browser sets `ctrlKey` on
 *   it even though no key is pressed, which is the de-facto way pinch is exposed on the web
 * - pinch in Safari — `gesturestart` / `gesturechange` / `gestureend`, carrying the scale
 *   relative to where the pinch started
 *
 * All of them have to be `preventDefault`-ed, otherwise the browser zooms the whole page as
 * well — which is why the wheel listener is registered by hand: React attaches `onWheel` as
 * a passive listener, and a passive listener cannot prevent anything.
 *
 * A touchpad fires far more events than there are frames, so the zoom is applied at most
 * once per animation frame, and persisted only once the gesture has settled — not one
 * backend request per event.
 */

/** Idle time after the last event before the zoom counts as finished and is persisted. */
const ZOOM_SETTLE_MS = 400

export type ZoomAnchor = {
    clientX: number
    clientY: number
}

/** Safari's pinch event — not part of the standard DOM typings. */
type GestureEvent = UIEvent & {
    scale: number
    clientX: number
    clientY: number
}

type Options = {
    ref: RefObject<HTMLElement>
    min: number
    max: number
    /** The zoom as the store has it right now — read on demand, never subscribed. */
    getZoom: () => number
    /**
     * Apply a new zoom, at most once per frame. Not persisted yet.
     *
     * @param anchor the pointer, which the view should keep the zoomed point under
     */
    onZoom: (zoom: number, anchor: ZoomAnchor) => void
    /** The wheel / pinch has settled — persist `zoom`. */
    onZoomEnd: (zoom: number) => void
}

const useWheelZoom = ({ ref, min, max, getZoom, onZoom, onZoomEnd }: Options): void => {
    // the listeners are attached once, so they must not close over stale callbacks
    const latest = useRef({ min, max, getZoom, onZoom, onZoomEnd })
    useLayoutEffect(() => {
        latest.current = { min, max, getZoom, onZoom, onZoomEnd }
    })

    useEffect(() => {
        const element = ref.current
        if (!element) return

        /** the zoom the events so far add up to, until the next frame applies it */
        let pendingZoom: number | null = null
        let anchor: ZoomAnchor = { clientX: 0, clientY: 0 }
        let frameId = 0
        let settleTimer: ReturnType<typeof setTimeout> | undefined
        /** set while a Safari pinch runs — its zoom is relative to this */
        let gestureStartZoom: number | null = null
        /** something was actually zoomed since the last persist — scrolling at a limit is not */
        let changed = false

        const clamp = (zoom: number) =>
            roundZoom(Math.min(Math.max(zoom, latest.current.min), latest.current.max))

        const applyPending = () => {
            frameId = 0
            if (pendingZoom === null) return
            const zoom = pendingZoom
            pendingZoom = null
            if (zoom === latest.current.getZoom()) return
            changed = true
            latest.current.onZoom(zoom, anchor)
        }

        const settle = () => {
            settleTimer = undefined
            if (!changed) return
            changed = false
            latest.current.onZoomEnd(latest.current.getZoom())
        }

        const zoomTo = (zoom: number, at: ZoomAnchor) => {
            pendingZoom = clamp(zoom)
            anchor = { clientX: at.clientX, clientY: at.clientY }
            if (!frameId) frameId = requestAnimationFrame(applyPending)
            if (settleTimer !== undefined) clearTimeout(settleTimer)
            settleTimer = setTimeout(settle, ZOOM_SETTLE_MS)
        }

        /** events in the same frame build on each other, not on the stale store value */
        const currentZoom = () => pendingZoom ?? latest.current.getZoom()

        const handleWheel = (event: WheelEvent) => {
            // a plain wheel keeps scrolling the view
            if (!event.ctrlKey && !event.metaKey) return
            event.preventDefault()
            // Safari reports the same pinch through the gesture events below
            if (gestureStartZoom !== null) return
            zoomTo(zoomByWheel(currentZoom(), wheelDeltaPx(event)), event)
        }

        const handleGestureStart = (event: Event) => {
            event.preventDefault()
            gestureStartZoom = currentZoom()
        }

        const handleGestureChange = (event: Event) => {
            event.preventDefault()
            if (gestureStartZoom === null) return
            const gesture = event as GestureEvent
            zoomTo(gestureStartZoom * gesture.scale, gesture)
        }

        const handleGestureEnd = (event: Event) => {
            event.preventDefault()
            gestureStartZoom = null
        }

        element.addEventListener("wheel", handleWheel, { passive: false })
        element.addEventListener("gesturestart", handleGestureStart)
        element.addEventListener("gesturechange", handleGestureChange)
        element.addEventListener("gestureend", handleGestureEnd)

        return () => {
            element.removeEventListener("wheel", handleWheel)
            element.removeEventListener("gesturestart", handleGestureStart)
            element.removeEventListener("gesturechange", handleGestureChange)
            element.removeEventListener("gestureend", handleGestureEnd)
            if (frameId) cancelAnimationFrame(frameId)
            // unmounted mid-zoom: whatever was already applied still has to be persisted
            if (settleTimer !== undefined) {
                clearTimeout(settleTimer)
                settle()
            }
        }
    }, [ref])
}

export default useWheelZoom
